import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Loader } from '../../../components';
import { AdminPopup } from '../../../components/admin';
import EmailComposer from '../../../components/admin/emailComposer';
import GradientStrip from '../../../components/gradientStrip';
import { adminApi, getRegistrationDetails, sendEmailMessage } from '../../../service';
import OtherGroupsRegistrationForm from '../../otherGroupsRegistrationForm';
import SchoolRegistrationForm from '../../schoolRegistrationForm';

const AdminRegistrationDetails = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [registrationData, setRegistrationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEmailPopupOpen, setIsEmailPopupOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailComposerKey, setEmailComposerKey] = useState(0);

  const { id: establishmentId } = useParams();

  const handleLogout = () => {
    try {
      // Remove the auth token from localStorage
      localStorage.removeItem('adminToken');

      // Remove the Authorization header
      delete adminApi.defaults.headers.common['Authorization'];

      // Show success message
      toast.success(t('logout.success') || 'Déconnexion réussie');

      // Navigate to login page
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error(t('logout.error') || 'Erreur lors de la déconnexion');
    }
  };

  const handleEmailClick = () => {
    try {
      if (!registrationData) return;

      // Get email from registration data
      const emailAddress =
        registrationData.emailConsent?.email || registrationData.inchargePersonDetails?.email;

      if (!emailAddress) {
        toast.error(t('email.noAddress') || 'Aucune adresse email disponible');
        return;
      }

      // Set the recipient email and show popup
      setRecipientEmail(emailAddress);
      setIsEmailPopupOpen(true);
      // Reset email composer
      setEmailComposerKey((prev) => prev + 1);
    } catch (error) {
      console.error('Email error:', error);
      toast.error(t('email.error') || "Erreur lors de l'ouverture du client email");
    }
  };

  const handleCloseEmailPopup = () => {
    setIsEmailPopupOpen(false);
    setIsSendingEmail(false);
  };

  const handleSendEmail = async (emailData) => {
    try {
      setIsSendingEmail(true);

      await sendEmailMessage({
        type: 'individual',
        establishmentId,
        to: recipientEmail,
        emailContent: {
          subject: emailData.subject,
          message: emailData.body,
        },
      });

      toast.success(t('email.sent') || 'Email envoyé avec succès');
      setIsEmailPopupOpen(false);
    } catch (error) {
      console.error('Send email error:', error);
      toast.error(t('email.sendError') || "Erreur lors de l'envoi de l'email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  useEffect(() => {
    const fetchRegistrationDetails = async () => {
      try {
        setLoading(true);
        const { data } = await getRegistrationDetails(establishmentId);

        // Format data similar to dashboard.jsx
        const { establishmentDetails, inchargePersons, emailConsent, classes } = data;

        if (!establishmentDetails?.registrationType) {
          setLoading(false);
          return;
        }

        const formattedData = {
          establishmentDetails: {
            ...establishmentDetails,
            privateContract: establishmentDetails.privateContract ? 'true' : 'false',
            representative: establishmentDetails.representative ? 'true' : 'false',
          },
          inchargePersonDetails: inchargePersons.find((person) => !person.secondIncharge),
          inchargePersonAnother: inchargePersons.find((person) => person.secondIncharge)
            ? 'false'
            : 'true',
          inchargePersonAnotherDetails: inchargePersons.find((person) => person.secondIncharge),
          classes: classes.map((classDetails) => ({
            ...classDetails,
            experienceDetails: {
              tourType: classDetails?.timeSlot?.tourType || 'introduction',
              timeSlot: classDetails?.timeSlot?.timeSlot || classDetails.arrivalTime,
              level: classDetails?.level,
            },
            bookExperience: 'true',
            handicap: classDetails.handicap ? 'true' : 'false',
            picnicOnSite: classDetails.picnicOnSite ? 'true' : 'false',
          })),
          emailConsent: {
            ...emailConsent,
            emailConsent: emailConsent.emailConsent ? 'true' : 'false',
            studentsSafetyConsent: emailConsent.studentsSafetyConsent ? 'true' : 'false',
            email:
              emailConsent.email || inchargePersons.find((person) => !person.secondIncharge)?.email,
          },
        };

        setRegistrationData(formattedData);
      } catch (error) {
        console.error('Error fetching registration details:', error);
        toast.error(
          t('registration.fetch.error') ||
            "Erreur lors de la récupération des détails d'inscription",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRegistrationDetails();
  }, [establishmentId, t]);

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white relative">
      <GradientStrip />
      <div className="container mx-auto py-6 px-4">
        <div className="flex flex-row justify-between items-center flex-wrap gap-6">
          {/* Left side - Heading and Subheading */}
          <div className="w-auto">
            <h1 className="font-poppins text-lg md:text-xl font-extrabold text-blue-10">
              PLATEFORME INSCRIPTION
            </h1>
            <p className="font-poppins text-lg text-blue-10">GROUPES GRAND EST 2026</p>
          </div>

          {/* Right side - Buttons */}
          <div className="flex items-center space-x-4">
            <button
              className="bg-lightBlue-10 text-blue-10 hover:scale-105 active:scale-95 transition-all duration-300 p-3 rounded-full"
              title="Email"
              onClick={handleEmailClick}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </button>
            <button
              className="bg-blue-10 text-white hover:scale-105 active:scale-95 transition-all duration-300 p-3 px-5 rounded-xl flex items-center gap-2"
              title="Logout"
              onClick={handleLogout}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              <span className="font-bold">DECONNEXION</span>
            </button>
          </div>
        </div>

        {/* Back button */}
        <div className="mt-6">
          <button
            onClick={handleBackClick}
            className="mr-4 bg-blue-10 text-white w-8 h-8 flex items-center justify-center rounded-lg shadow-sm hover:bg-blue-20 hover:scale-105 active:scale-95 transition-all duration-300">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
        </div>

        {/* Display the appropriate form based on registration type */}
        {loading ? (
          <Loader backgroundClassName="bg-teal-10/80" />
        ) : registrationData ? (
          <div className="mt-6">
            {registrationData.establishmentDetails.registrationType === 'other-group' ? (
              <OtherGroupsRegistrationForm storedData={registrationData} isAdminView={true} />
            ) : (
              <SchoolRegistrationForm storedData={registrationData} isAdminView={true} />
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p>Aucune donnée d'inscription disponible</p>
          </div>
        )}
      </div>

      {/* Email Composer Popup */}
      <AdminPopup
        open={isEmailPopupOpen}
        closePopup={isSendingEmail ? null : handleCloseEmailPopup}
        variant="teal"
        title="ENVOYER UN EMAIL"
        subTitle="Rédiger le contenu de l'e-mail"
        customHeightClasses="h-auto"
        customWidthClasses="max-w-3xl">
        <EmailComposer
          key={emailComposerKey}
          onSend={handleSendEmail}
          isSending={isSendingEmail}
          toEmail={recipientEmail}
        />
      </AdminPopup>
    </div>
  );
};

export default AdminRegistrationDetails;
